// src/components/ExpenseKPI.tsx
import React, { useMemo } from 'react';
import { Row, Col } from 'antd';
import { DollarCircleOutlined, CalendarOutlined, FieldTimeOutlined } from '@ant-design/icons';
import StatCard from './StatCard';
import { useExpenses } from '../hooks/useExpenses';
import { D, W, M, Y, currPrevSum, pct } from '../utils/kpi';

// нормализация суммы расхода из разных форматов
const expenseTotal = (e: any): number => {
  if (typeof e?.total === 'number') return e.total;
  if (typeof e?.totalCost === 'number') return e.totalCost;
  if (Array.isArray(e?.lines) && e.lines.length) {
    return e.lines.reduce((s: number, l: any) => {
      const qty = Number(l?.qty ?? 0);
      const unit = l?.unitCost != null ? Number(l.unitCost) : null;
      const lineTotal =
        l?.totalCost != null
          ? Number(l.totalCost)
          : unit != null
          ? unit * qty
          : 0;
      return s + (Number.isFinite(lineTotal) ? lineTotal : 0);
    }, 0);
  }
  return 0;
};

type NormItem = { createdAt: string; total: number };

type Props = {
  /** Показать 5-ю карточку «За всё время» */
  showAllTime?: boolean;
  /** (опц.) Отфильтрованный источник — для синхронизации с таблицей/донатом */
  source?: any[];
};

const fmt = (n: number) => n.toLocaleString('ru-RU');

const ExpenseKPI: React.FC<Props> = ({ showAllTime = false, source }) => {
  const hook = useExpenses(10_000);
  const raw = source ?? hook.items;

  // приводим расходы к унифицированному виду { createdAt, total }
  const norm: NormItem[] = useMemo(
    () =>
      (raw ?? []).map((e: any) => ({
        createdAt: e.createdAt,
        total: expenseTotal(e),
      })),
    [raw]
  );

  const data = useMemo(
    () => ({
     d: currPrevSum<NormItem>(norm, D),
     w: currPrevSum<NormItem>(norm, W),
     m: currPrevSum<NormItem>(norm, M),
     y: currPrevSum<NormItem>(norm, Y),
      all: {
        curr: norm.reduce((s, n) => s + (n.total || 0), 0),
        prev: 0,
      },
    }),
    [norm]
  );

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} md={12} xxl={6}>
        <StatCard
          className="stat-card--kpi"
          accent
          icon={<FieldTimeOutlined />}
          title="Расход (24 часа)"
          value={fmt(data.d.curr)}
          suffix="AMD"
          linkText="Отчёт"
          onLink={() => {}}
          deltaText={pct(data.d.curr, data.d.prev)}
          deltaHint="vs вчера"
        />
      </Col>

      <Col xs={24} md={12} xxl={6}>
        <StatCard
          className="stat-card--kpi"
          accent
          icon={<CalendarOutlined />}
          title="Расход (неделя)"
          value={fmt(data.w.curr)}
          suffix="AMD"
          linkText="Отчёт"
          onLink={() => {}}
          deltaText={pct(data.w.curr, data.w.prev)}
          deltaHint="vs прошл. неделя"
        />
      </Col>

      <Col xs={24} md={12} xxl={6}>
        <StatCard
          className="stat-card--kpi"
          accent
          icon={<CalendarOutlined />}
          title="Расход (месяц)"
          value={fmt(data.m.curr)}
          suffix="AMD"
          linkText="Отчёт"
          onLink={() => {}}
          deltaText={pct(data.m.curr, data.m.prev)}
          deltaHint="vs прошл. месяц"
        />
      </Col>

      <Col xs={24} md={12} xxl={6}>
        <StatCard
          className="stat-card--kpi"
          accent
          icon={<CalendarOutlined />}
          title="Расход (год)"
          value={fmt(data.y.curr)}
          suffix="AMD"
          linkText="Отчёт"
          onLink={() => {}}
          deltaText={pct(data.y.curr, data.y.prev)}
          deltaHint="vs прошл. год"
        />
      </Col>

      {showAllTime && (
        <Col xs={24} md={12} xxl={6}>
          <StatCard
            className="stat-card--kpi"
            blueAccent
            icon={<DollarCircleOutlined />}
            title="Расход (за всё время)"
            value={fmt(data.all.curr)}
            suffix="AMD"
            linkText="Детализация"
            onLink={() => {}}
          />
        </Col>
      )}
    </Row>
  );
};

export default ExpenseKPI;
